import { useSyncExternalStore } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import {
	ShieldAlert,
	WifiOff,
	LogIn,
	RotateCw,
	MessageCircle,
	type LucideIcon,
} from "lucide-react";
import {
	subscribeToConnectionOverlay,
	getConnectionOverlaySnapshot,
	setIntentionalSignOut,
	supabase,
	type ConnectionOverlayStatus,
} from "./supabaseClient";

type Content = {
	Icon: LucideIcon;
	iconClass: string;
	title: string;
	description: string;
	ActionIcon: LucideIcon;
	actionLabel: string;
};

const CONTENT: Record<Exclude<ConnectionOverlayStatus, null>, Content> = {
	session_expired: {
		Icon: ShieldAlert,
		iconClass: "text-red-500",
		title: "Sitzung abgelaufen",
		description:
			"Ihre Anmeldung ist nicht mehr gültig. Bitte melden Sie sich erneut an, um fortzufahren.",
		ActionIcon: LogIn,
		actionLabel: "Neu anmelden",
	},
	connection_error: {
		Icon: WifiOff,
		iconClass: "text-amber-500",
		title: "Keine Verbindung zum Server",
		description:
			"Es konnte keine Verbindung zu dentDESK hergestellt werden. Bitte prüfen Sie Ihre Internetverbindung und versuchen Sie es erneut.",
		ActionIcon: RotateCw,
		actionLabel: "Erneut versuchen",
	},
};

export const ConnectionOverlay: React.FC = () => {
	const { status } = useSyncExternalStore(
		subscribeToConnectionOverlay,
		getConnectionOverlaySnapshot
	);

	if (!status) return null;

	const content = CONTENT[status];
	const { Icon, ActionIcon } = content;

	const handlePrimary = async () => {
		if (status === "session_expired") {
			setIntentionalSignOut(true);
			try {
				await supabase.auth.signOut();
			} finally {
				window.location.replace("/");
			}
		} else {
			window.location.reload();
		}
	};

	const handleSupport = () => {
		const message = "Ich habe ein Verbindungsproblem in dentDESK.";
		if (window.Intercom) {
			window.Intercom("showNewMessage", message);
		}
	};

	return (
		<DialogPrimitive.Root open>
			<DialogPrimitive.Portal container={document.body}>
				<DialogPrimitive.Overlay
					className="fixed inset-0 bg-black/80"
					style={{ zIndex: 2147483000 }}
				/>
				<DialogPrimitive.Content
					className="fixed left-1/2 top-1/2 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg border bg-white p-6 shadow-lg"
					style={{ zIndex: 2147483001 }}
					onEscapeKeyDown={(e) => e.preventDefault()}
					onPointerDownOutside={(e) => e.preventDefault()}
					onInteractOutside={(e) => e.preventDefault()}
					aria-label={content.title}
					data-testid={`connection-overlay-${status}`}
				>
					<div className="flex items-start gap-4">
						<Icon className={`h-10 w-10 shrink-0 ${content.iconClass}`} />
						<div>
							<DialogPrimitive.Title className="text-lg font-semibold">
								{content.title}
							</DialogPrimitive.Title>
							<DialogPrimitive.Description className="mt-2 text-sm text-gray-600">
								{content.description}
							</DialogPrimitive.Description>
						</div>
					</div>

					<div className="mt-6 flex flex-col gap-3">
						<button
							onClick={handlePrimary}
							className="flex items-center justify-center gap-2 rounded-md bg-blue-600 px-4 py-3 text-sm font-medium text-white hover:bg-blue-700 transition-colors"
							aria-label={content.actionLabel}
							data-testid="connection-overlay-action"
						>
							<ActionIcon className="h-4 w-4" />
							{content.actionLabel}
						</button>
						<button
							onClick={handleSupport}
							className="flex items-center justify-center gap-2 rounded-md bg-gray-100 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-200 transition-colors"
							data-testid="connection-overlay-support"
						>
							<MessageCircle className="h-4 w-4" />
							Support-Chat öffnen
						</button>
					</div>
				</DialogPrimitive.Content>
			</DialogPrimitive.Portal>
		</DialogPrimitive.Root>
	);
};
